import React from 'react';
import { Box, Container, Typography, Button, Grid, Chip } from '@mui/material';
import { Link as NavLink } from "react-scroll";
import CodeIcon from '@mui/icons-material/Code';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ScienceIcon from '@mui/icons-material/Science';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// Helper function to get AppBar height - adjust if your AppBar height is dynamic
const getAppBarHeight = () => 64; // Assuming 64px

export default function HeroSection() {
  const appBarHeight = getAppBarHeight();
  
  const keyBenefits = [
    "Bespoke Solutions Tailored to Your Needs",
    "Cutting-Edge Technology & Best Practices", 
    "Data-Driven Insights for Strategic Decisions",
    "Scalable & Future-Proof Architecture"
  ];

  const highlights = [
    { icon: <CodeIcon sx={{ fontSize: '2.5rem', color: '#60a5fa' }} />, label: "Development", value: "Bespoke Software" },
    { icon: <PsychologyIcon sx={{ fontSize: '2.5rem', color: '#60a5fa' }} />, label: "Intelligence", value: "Data Science & Innovation" },
    { icon: <ScienceIcon sx={{ fontSize: '2.5rem', color: '#60a5fa' }} />, label: "Innovation", value: "Research Excellence" }
  ];
  
  return (
    <Box 
      className="heroSection snap-section"
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: `calc(100vh - ${appBarHeight}px)`,
        background: 'linear-gradient(135deg, #1e293b 0%, #334155 50%, #2561b3 100%)',
        color: 'white',
        paddingTop: `${appBarHeight}px`,
        overflow: 'hidden'
      }}
    >
      {/* Animated background elements - Enhanced visibility */}
      <Box sx={{
        position: 'absolute',
        top: '10%',
        right: '10%',
        width: 400,
        height: 400,
        background: 'linear-gradient(135deg, rgba(37, 97, 179, 0.4), rgba(96, 165, 250, 0.3))',
        borderRadius: '50%',
        animation: 'floatAndPulse 8s ease-in-out infinite',
        filter: 'blur(40px)'
      }} />
      <Box sx={{
        position: 'absolute',
        bottom: '20%',
        left: '5%',
        width: 300,
        height: 300,
        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.3), rgba(37, 97, 179, 0.4))',
        borderRadius: '50%',
        animation: 'floatAndPulse 6s ease-in-out infinite reverse',
        filter: 'blur(30px)'
      }} />
      
      {/* Additional geometric shapes - Enhanced */}
      <Box sx={{
        position: 'absolute',
        top: '30%',
        left: '15%',
        width: 150,
        height: 150,
        background: 'linear-gradient(45deg, rgba(96, 165, 250, 0.2), rgba(37, 97, 179, 0.1))',
        transform: 'rotate(45deg)',
        animation: 'floatRotateAndPulse 12s ease-in-out infinite',
        filter: 'blur(20px)'
      }} />
      <Box sx={{
        position: 'absolute',
        bottom: '30%',
        right: '20%',
        width: 120,
        height: 120,
        background: 'linear-gradient(45deg, rgba(37, 97, 179, 0.2), rgba(96, 165, 250, 0.1))',
        borderRadius: '50%',
        animation: 'floatAndPulse 10s ease-in-out infinite reverse',
        filter: 'blur(25px)'
      }} />
      
      {/* Additional floating elements for more movement */}
      <Box sx={{
        position: 'absolute',
        top: '60%',
        left: '8%',
        width: 100,
        height: 100,
        background: 'linear-gradient(135deg, rgba(96, 165, 250, 0.15), transparent)',
        borderRadius: '50%',
        animation: 'floatAndPulse 14s ease-in-out infinite',
        filter: 'blur(30px)'
      }} />
      <Box sx={{
        position: 'absolute',
        top: '20%',
        right: '30%',
        width: 80,
        height: 80,
        background: 'linear-gradient(45deg, rgba(37, 97, 179, 0.15), transparent)',
        transform: 'rotate(30deg)',
        animation: 'floatRotateAndPulse 16s ease-in-out infinite reverse',
        filter: 'blur(20px)'
      }} />
      <Box sx={{
        position: 'absolute',
        bottom: '10%',
        right: '10%',
        width: 60,
        height: 60,
        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2), transparent)',
        borderRadius: '50%',
        animation: 'floatAndPulse 9s ease-in-out infinite',
        filter: 'blur(15px)'
      }} />

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
        <Grid container spacing={6} alignItems="center">
          {/* Main Content - Left Side */}
          <Grid item xs={12} lg={8}>
            <Box sx={{ textAlign: { xs: 'center', lg: 'left' } }}>
              {/* Company Badge */}
              <Chip 
                label="Leading Software & Data Science Consultancy"
                sx={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.1) 100%)',
                  color: 'white',
                  fontWeight: 600,
                  marginBottom: 3,
                  marginTop: 5,
                  fontSize: '0.9rem',
                  border: '1px solid rgba(255,255,255,0.3)',
                  backdropFilter: 'blur(10px)'
                }}
              />
              <br/>
              
              <Typography 
                variant="h1" 
                component="h1" 
                sx={{ 
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, #ffffff 0%, #e2e8f0 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                  marginBottom: 3,
                  fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem', lg: '5rem' },
                  lineHeight: 1.1,
                  position: 'relative',
                  display: 'inline',
                }}
              >
                Innovative Software & Data Science Solutions
                <Box
                  component="span"
                  sx={{
                    display: 'inline-block',
                    width: { xs: '16px', sm: '20px', md: '24px', lg: '28px' },
                    height: { xs: '16px', sm: '20px', md: '24px', lg: '28px' },
                    backgroundColor: '#ffffff',
                    marginLeft: '8px',
                    animation: 'blink 1.2s infinite',
                    verticalAlign: 'middle',
                    marginTop: { xs: '8px', sm: '12px', md: '16px', lg: '20px' },
                    
                  }}
                />
              </Typography>
              
              <Typography 
                variant="h5" 
                component="p" 
                sx={{ 
                  marginTop: 2,
                  marginBottom: 4, 
                  color: 'rgba(255, 255, 255, 0.9)',
                  lineHeight: 1.6,
                  fontWeight: 400,
                  fontSize: { xs: '1.3rem', md: '1.6rem' },
                  maxWidth: '700px',
                  marginX: { xs: 'auto', lg: 0 }
                }}
              >
                We craft bespoke software and unlock data-driven insights to propel your business forward. Partner with Datasky to transform your vision into reality.
              </Typography>

              {/* Key Benefits List */}
              <Box sx={{ marginBottom: 5 }}>
                <Grid container spacing={2}>
                  {keyBenefits.map((benefit, index) => (
                    <Grid item xs={12} sm={6} key={index}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        justifyContent: { xs: 'start', lg: 'flex-start' }
                      }}>
                        <CheckCircleIcon sx={{ 
                          color: '#60a5fa', 
                          marginRight: 2, 
                          fontSize: '1.5rem'
                        }} />
                        <Typography variant="body1" sx={{ 
                          color: 'rgba(255, 255, 255, 0.9)',
                          fontWeight: 500,
                          fontSize: '1.1rem'
                        }}>
                          {benefit}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>

              <NavLink 
                to="contact-section" 
                spy
                smooth
                offset={-appBarHeight}
                duration={500}
                style={{ textDecoration: 'none' }}
              >
                <Button 
                  variant="contained" 
                  size="large" 
                  sx={{
                    background: 'linear-gradient(135deg, #2561b3 0%, #3b82f6 100%)',
                    color: 'white',
                    fontWeight: 700,
                    paddingX: 6,
                    paddingY: 2.5,
                    fontSize: '1.2rem',
                    borderRadius: 3,
                    textTransform: 'none',
                    boxShadow: '0 8px 25px rgba(37, 97, 179, 0.4)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1e40af 0%, #2563eb 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 12px 35px rgba(37, 97, 179, 0.6)'
                    }
                  }}
                >
                  Get Started Today
                </Button>
              </NavLink>
            </Box>
          </Grid>
          
          {/* Visual Highlights - Right Side */}
          <Grid item xs={12} lg={4}>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { 
                xs: '1fr', 
                sm: 'repeat(2, 1fr)', 
                md: 'repeat(2, 1fr)', 
                lg: '1fr' 
              },
              gap: { xs: 2, sm: 3 },
              padding: { xs: 2, sm: 4, lg: 0 },
              justifyItems: 'center'
            }}>
              {/* Highlight Cards */}
              {highlights.map((highlight, index) => (
                <Box 
                  key={index}
                  sx={{
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                    backdropFilter: 'blur(20px)',
                    borderRadius: 3,
                    padding: { xs: 2, sm: 3 },
                    border: '1px solid rgba(255,255,255,0.2)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.3)',
                    width: '100%',
                    maxWidth: { xs: '240px', sm: '280px' },
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 15px 35px rgba(0,0,0,0.4)'
                    }
                  }}
                >
                  <Box sx={{ marginBottom: { xs: 1.5, sm: 2 } }}>
                    {highlight.icon}
                  </Box>
                  <Typography variant="h6" sx={{ 
                    fontWeight: 700,
                    marginBottom: 1,
                    color: 'white',
                    fontSize: { xs: '1rem', sm: '1.25rem' }
                  }}>
                    {highlight.value}
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontWeight: 500,
                    fontSize: { xs: '0.8rem', sm: '0.875rem' }
                  }}>
                    {highlight.label}
                  </Typography>
                </Box>
              ))}
              
              {/* Stats Badge */}
              <Box sx={{
                background: 'linear-gradient(135deg, rgba(37, 97, 179, 0.3) 0%, rgba(59, 130, 246, 0.2) 100%)',
                backdropFilter: 'blur(20px)',
                borderRadius: 3,
                padding: { xs: 2, sm: 3 },
                border: '1px solid rgba(96, 165, 250, 0.3)',
                boxShadow: '0 8px 25px rgba(37, 97, 179, 0.3)',
                width: '100%',
                maxWidth: { xs: '240px', sm: '280px' },
                textAlign: 'center',
                gridColumn: { xs: 'span 1', sm: 'span 2', lg: 'span 1' }
              }}>
                <Typography variant="h3" sx={{ 
                  fontWeight: 800,
                  color: '#60a5fa',
                  marginBottom: 1,
                  fontSize: { xs: '2rem', sm: '3rem' }
                }}>
                  10+
                </Typography>
                <Typography variant="body1" sx={{ 
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontWeight: 600,
                  fontSize: { xs: '0.9rem', sm: '1rem' }
                }}>
                  Years of Combined Expertise
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
      
      {/* Add keyframes for floating animation and cursor blink */}
      <style>{`
        @keyframes floatAndPulse {
          0%, 100% { 
            transform: translateY(0px) scale(1); 
          }
          25% { 
            transform: translateY(-25px) scale(1.1); 
          }
          50% { 
            transform: translateY(-15px) scale(0.9); 
          }
          75% { 
            transform: translateY(-30px) scale(1.05); 
          }
        }
        @keyframes floatRotateAndPulse {
          0%, 100% { 
            transform: translateY(0px) rotate(45deg) scale(1); 
          }
          25% { 
            transform: translateY(-25px) rotate(47deg) scale(1.15); 
          }
          50% { 
            transform: translateY(-15px) rotate(44deg) scale(0.85); 
          }
          75% { 
            transform: translateY(-30px) rotate(46deg) scale(1.1); 
          }
        }
        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
      `}</style>
    </Box>
  );
} 